import { computed } from 'vue'
import { useStore } from 'vuex'
import { notify } from '@kyvg/vue3-notification'
import i18n from '@/locales'
import { createWidgetPdfExport, getExportFile, getWidgetPdfExport } from '@/controllers/library'
import { WidgetTypes } from '@/interfaces/global/widgets'

const POLL_INTERVAL_MS = 3000
const MAX_RETRY_INTERVAL_MS = 30000
const FINISHED_JOB_STATES = ['COMPLETED', 'COMPLETED_WITH_ERROR', 'CANCELED']
const EXPORT_FLAGS: Record<string, string> = {
  book: 'allowBookExport',
  chapter: 'allowChapterExport',
  illustration: 'allowPageExport'
}

export function useWidgetExport () {
  const store = useStore()

  const canExport = computed(() => {
    const content = store.state.content
    const isExportDisabled = content?.disableExport ||
      content?.toc?.book?.disableExport ||
      store.getters['user/hasDisablePrinting'] ||
      (content?.contentType === 'book' && store.getters['user/hasDisableBookPrinting'])

    return !!store.state.widgets.widgetConfig?.[EXPORT_FLAGS[content?.contentType]] && !isExportDisabled
  })

  async function exportContent () {
    const content = store.state.content
    const name = content.name
    const isPageWidget = store.getters['widgets/widgetType'] === WidgetTypes.page

    try {
      const jobId = await createWidgetPdfExport({
        mediaId: content.contentType === 'book' ? content.id : isPageWidget ? undefined : content.toc?.book?.id,
        chapterId: content.contentType === 'chapter' ? content.id : undefined,
        pageId: content.contentType === 'illustration' ? content.id : undefined
      })
      notify({ group: 'widget', title: i18n.global.t('widgetExportStarted', { name }), type: 'success', duration: 5000 })

      const blob = await getExportFile(await waitForExportFile(jobId))
      window.parent.postMessage(
        { action: 'downloadFile', item: { blob, name: `${name}.pdf` } },
        store.getters['widgets/targetUri'])
    } catch {
      notify({ group: 'widget', title: i18n.global.t('widgetExportFailed', { name }), type: 'error', duration: 10000 })
    }
  }

  return { canExport, exportContent }
}

async function waitForExportFile (jobId: number) {
  let interval = POLL_INTERVAL_MS
  for (;;) {
    await new Promise(resolve => setTimeout(resolve, interval))
    let job
    try {
      job = await getWidgetPdfExport(jobId)
    } catch (error) {
      if (!isTransientError(error)) throw error
      interval = Math.min(interval * 2, MAX_RETRY_INTERVAL_MS)
      continue
    }
    interval = POLL_INTERVAL_MS

    if (FINISHED_JOB_STATES.includes(job.state)) {
      const file = job.state === 'COMPLETED' ? job.outputFile : undefined
      if (!file) throw new Error(`Export ${jobId} finished without a file`)
      return file
    }
  }
}

function isTransientError (error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status
  return status === undefined || status === 429 || status >= 500
}
